import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient } from "react-query";
import { getLocales } from './core/utils/app';
import { GetCookie, SetCookie} from './core/utils/cookies';
import { consumePendingSignUpEvent } from './core/utils/helper';
import { aiproFlags } from './hooks/flags';
import i18n from './i18n';
import './tailwind.scss';
import './index.css';
import 'segoe-fonts';
import GlobalProvider from './core/context/GlobalProvider';
import { useDissidia } from './hooks/useDissidia';

const Home = lazy(() =>  import("./home/index_v2.jsx"))
const Register = lazy(() => import("./register/index_flow_01"));
const RegisterD = lazy(() => import("./register/index_flow_04"));
const Pricing = lazy(() => import("./pricing/index.jsx"));
const PricingD = lazy(() => import("./pricing/index.jsx"));
const Pay = lazy(() => import("./pay"));
const PayV1 = lazy(() => import("./pay/index_01"));
const PayV2 = lazy(() => import("./pay/index_02"));
const PayV3 = lazy(() => import("./pay/index_03"));
const PayV4 = lazy(() => import("./pay/index_04"));
const PayV6 = lazy(() => import("./pay/index_06"));
const PayRef = lazy(() => import("./pay-reference/index"));
const PayConfirm = lazy(() => import("./pay-confirm/index"));
const Thankyou = lazy(() => import("./thankyou"));
const ThankyouV2 = lazy(() => import("./thankyou/index_v2"));
const ConditionalThankyou = () => {
  const locales = GetCookie('locales') || 'en';
  
  // If locales is 'en' or no value, use ThankyouV2, otherwise use Thankyou
  if (locales === 'en' || !locales) {
    return <ThankyouV2 />;
  } else {
    return <Thankyou />;
  }
};
const StartChatGPT = lazy(() => import("./lp/start-chat-gpt"));
const StartChatGPT3 = lazy(() => import("./lp/start-chatgpt-v3"));
const StartChatGPT4 = lazy(() => import("./lp/start-chatgpt-v4"));
const SplashPage1 = lazy(() => import("./splashscreen/wp_splash"));
const Disclaimer = lazy(() => import("./footer/disclaimer"));
const SelectAccount = lazy(() => import("./select-account/index"));
const NotFoundPage = lazy(() => import("./404"));
const Header = lazy(() => import("./header/basilisk/v2/index"));
const RedirectLogin = lazy(() => import("./redirect-login/index"));
const SelectAccount2 = lazy(() => import("./select-account-b/index"));
const AccountTypeSurvey = lazy(() => import("./account-type-survey/index"));

if (typeof window !== "undefined" && window.flags) {
  aiproFlags.flags = window.flags;
}

const ConditionalPopup = () => {
  const location = useLocation();
  const LatestPopup = useDissidia("LatestUpdatesPopup");
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const strapi_api_url = process.env.REACT_STRAPI_API_URL ?? '';
  const strapi_api_token = process.env.REACT_STRAPI_API_TOKEN ?? '';

  if (isHomePage && LatestPopup) {
    return (
      <LatestPopup
        apiUrl={strapi_api_url}
        apiToken={strapi_api_token}
      />
    );
  }

  return null;
};

const isPricingPage = window.location.pathname === '/subscription-plan';
const ppg = GetCookie("ppg") ? GetCookie("ppg") : process.env.REACT_APP_DEFAULT_PPG ? process.env.REACT_APP_DEFAULT_PPG : "14";
// const splashCookie = GetCookie("splash") ? GetCookie("splash") : "";
const queryClient = new QueryClient();

function App() {
  const [existingCookie, setExistingCookie] = useState('');
  let currentLocation = window.location.href;

  useEffect(() => {     
    if(window.flags) {
      aiproFlags.flags = window.flags;
      for(var k in aiproFlags.flags) {
        if(aiproFlags.flags[k]){
          SetCookie(k, aiproFlags.flags[k],  { domain: '.ai-pro.org', path: '/' });
        }
      }
    }
      // Initial setup
      getLocales();
      consumePendingSignUpEvent();
      if(aiproFlags.flags['mode'] === 'test') SetCookie("mode", aiproFlags.flags['mode'], {path: '/', domain: '.ai-pro.org'});
      // Check for URL parameters and update i18n if needed
      setExistingCookie(GetCookie('locales'));

      let validLocales = [
        'en', 'ar', 'de', 'es', 'fr', 'it', 'pl', 'pt', 'sv', 'tr',
      ];

      if (existingCookie && validLocales.includes(existingCookie)) {
        // Only update if different from current language
          console.log("Changing language to:", existingCookie);
          i18n.changeLanguage(existingCookie);
      }
      setTimeout(() => {
        document.querySelector("#footer-placeholder").style.display = 'block';
      }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (window.location.pathname === "/my-account") {
        document.body.style.fontFamily = "'Poppins', sans-serif";
        document.body.style.backgroundColor = "white";
      }
      else if (ppg === '121' && isPricingPage) {
        document.body.style.fontFamily = "'Inter', sans-serif";
        document.body.style.backgroundColor = "transparent";
      } else {
        document.body.style.fontFamily = "'Poppins', sans-serif";
        // document.body.style.backgroundColor = "#F3F4F6";
      }
    }, []);


  return (

    <Router>
      <div>
        <GlobalProvider
          queryClient={queryClient}
          subscription={{
            showFlashSale: true,
          }}
        >
          <Suspense fallback={null}>
            <ConditionalPopup />
            <div dir={(existingCookie === 'ar' ? "rtl" : "")} className={`md:min-h-[85vh]`}>
              <Routes>
                <Route path="/" element={<><Header/><Home /></>} />
                <Route path="/register" element={<Register />} />
                <Route path="/register-d" element={<RegisterD />} />
                {/* <Route path="/compare-pricing" element={<Pricing />} /> */}
                <Route path="/subscription-plan" element={<Pricing />} />
                <Route path="/plans-and-pricing-d" element={<PricingD />} />
                <Route path="/pay" element={<Pay />} />
                <Route path="/pay/1LCXJMZNX6" element={<Pay />} />
                <Route path="/pay/bH0w05VJXk" element={<PayV1 />} />
                <Route path="/pay/VS6lni4hKx" element={<PayV2 />} />
                <Route path="/pay/uHTinVqsUl" element={<PayV3 />} />
                <Route path="/checkout-page-rec" element={<Pay />} />
                <Route path="/checkout-page-apple-google" element={<PayV3 />} />
                <Route path="/checkout-multi-lang" element={<PayV3 />} />
                <Route path="/pay/mcWiDilmgQ" element={<PayV4 />} />
                <Route path="/pay/FSQ6N8V2A" element={<PayV6 />} />
                <Route path="/payment-reference" element={<PayRef />} />
                <Route path="/payment-confirm" element={<PayConfirm />} />
                <Route path="/thankyou" element={<ConditionalThankyou />} />
                <Route path="/start-chat-gpt" element={<StartChatGPT />} />
                <Route path="/start-chatgpt-v3" element={<StartChatGPT3 />} />
                <Route path="/start-chatgpt-v4" element={<StartChatGPT4 />} />
                <Route path="/splash" element={<SplashPage1 />} />
                <Route path="/select-account-type-d" element={<SelectAccount />} />
                <Route path="/select-account-type" element={<SelectAccount2 />} />
                <Route path="/account-type-survey" element={<AccountTypeSurvey />} />
                <Route path="/redirect-my-account" element={<RedirectLogin />} />
                {/* always last */}
                <Route path="/*" element={<NotFoundPage />} />
              </Routes>
            </div>
            {(!currentLocation.includes('enterprise-plan') || currentLocation.includes('thankyou')) && 
              <Disclaimer/>
            }
          </Suspense>

        </GlobalProvider>
        <div className="loader-container"><div className="overlay"></div><div className="lds-ai"><div></div><div></div><div></div></div></div>
      </div>
    </Router>
  );
}
export default App;